'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';

const INITIAL_COUNT = 6;
const LOAD_MORE_COUNT = 6;

const SuccessStoryGrid = ({ successData }) => {
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

  const allStories =
    successData?.cases
      ?.map((item) => item?.case_studies_id)
      ?.filter(Boolean) || [];

  const stories = allStories.slice(0, visibleCount);
  const hasMore = allStories.length > visibleCount;
  const hasLoadedMore = visibleCount > INITIAL_COUNT;
  const exploreHref = successData?.link ?? '/case-study';
  const exploreLabel = successData?.btn || 'Explore More';

  if (!allStories.length) return null;

  return (
    <section className="successStory w-full max-w-screen-xl mx-auto z-10 px-4 flex flex-col items-center gap-8 md:gap-12">
      <div className="w-full max-w-[760px] text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white">
          {successData?.heading}
        </h2>
        <p className="mx-auto mt-4 max-w-[680px] text-sm md:text-base leading-7 text-[#D7E0E2]">
          {successData?.description}
        </p>
      </div>

      <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-3">
        {stories.map((story) => {
          const mediaKey = story?.thumbnail ?? story?.file;
          const storyUrl = story?.button_url ?? '/case-study';

          return (
            <article
              key={story?.id ?? story?.heading}
              className="group flex h-full min-h-[500px] flex-col overflow-hidden rounded-xl border border-white/10 bg-[#041112] transition-all duration-300 hover:border-[#1FBCCB]/30 hover:bg-[#061b1d] hover:shadow-[0_4px_30px_rgba(31,188,203,0.1)]"
            >
              <Link href={storyUrl} className="block">
                <div className="relative h-[260px] w-full overflow-hidden bg-[#071b1d]">
                  {mediaKey ? (
                    <Image
                      src={`${process.env.NEXT_PUBLIC_API_URL}/assets/${mediaKey}`}
                      alt={story?.heading ?? 'Success story'}
                      fill
                      sizes="(min-width: 1024px) 560px, 100vw"
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-full w-full bg-gradient-to-br from-[#071b1d] via-[#0b2f33] to-[#020c0d]" />
                  )}
                </div>
              </Link>

              <div className="flex flex-1 flex-col p-5">
                <div className="flex flex-1 flex-col">
                  {story?.tags?.length ? (
                    <div className="mb-4 flex min-h-[68px] flex-wrap content-start gap-2">
                      {story.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="h-fit rounded-full border border-white/10 bg-white/[0.05] px-3 py-1 text-xs text-[#D7E0E2]"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="mb-4 min-h-[68px]" />
                  )}

                  <div>
                    <h3 className="text-xl font-bold leading-7 text-white line-clamp-2">
                      {story?.heading}
                    </h3>
                    <p className="mt-3 text-sm leading-6 text-[#B8C4C7] line-clamp-4">
                      {story?.description}
                    </p>
                  </div>
                </div>

                <Link
                  href={storyUrl}
                  className="mt-6 inline-flex w-fit items-center rounded-full border border-white/25 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white hover:text-black"
                >
                  Read Case Study
                </Link>
              </div>
            </article>
          );
        })}
      </div>

      {hasMore && !hasLoadedMore ? (
        <button
          type="button"
          onClick={() => setVisibleCount((count) => count + LOAD_MORE_COUNT)}
          className="inline-flex items-center justify-center rounded-full border border-white/35 px-8 py-4 text-sm md:text-base font-medium text-white hover:bg-white hover:text-black transition-all duration-200"
        >
          View More
        </button>
      ) : (
        <Link
          href={exploreHref}
          className="inline-flex items-center justify-center rounded-full border border-white/35 px-8 py-4 text-sm md:text-base font-medium text-white hover:bg-white hover:text-black transition-all duration-200"
        >
          {exploreLabel}
        </Link>
      )}
    </section>
  );
};

export default SuccessStoryGrid;
